import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Factory, Loader2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmtNum } from "@/lib/api";
import { Panel } from "@/components/common";

export const EU = "AT BE BG CY CZ DE DK EE ES FI FR GR HR HU IE IT LT LU LV MT NL PL PT RO SE SI SK".split(" ");

export const MillCountry = ({ s, canEdit, onSaved }) => {
  const mc = s.mill_country || s.country;
  const [v, setV] = useState(mc || "");
  const [edit, setEdit] = useState(false);
  const save = async () => {
    try { await api.put(`/suppliers/${s.id}/mill-country`, { country: v }); toast.success("Mill country saved — origins are being re-checked"); setEdit(false); onSaved?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="supplier-mill-country">
      <Factory className="h-3.5 w-3.5 text-slate-400" />Mill / dispatch country{" "}
      {edit ? (
        <><Input data-testid="mill-country-input" value={v} maxLength={2} onChange={(e) => setV(e.target.value.toUpperCase())} className="h-7 w-16 uppercase" />
          <Button size="sm" className="h-7" data-testid="mill-country-save" onClick={save}>Save</Button><button className="text-xs text-slate-500" onClick={() => setEdit(false)}>Cancel</button></>
      ) : (
        <><b data-testid="mill-country-value">{mc || "—"}</b>{EU.includes(mc) ? <span className="rounded bg-sky-50 px-1.5 text-[11px] font-semibold text-sky-700">EU</span> : <span className="rounded bg-slate-100 px-1.5 text-[11px] font-semibold text-slate-600">non-EU</span>}
          {canEdit && <button className="text-xs text-[#D52B3B] hover:underline" data-testid="mill-country-edit" onClick={() => setEdit(true)}>edit</button>}</>
      )}
    </span>
  );
};

export const MillCountryImport = ({ onDone }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const run = async (f) => {
    const fd = new FormData(); fd.append("file", f); setBusy(true);
    try {
      const r = await api.post("/suppliers/mill-countries", fd);
      const rej = r.data.rejected;
      const desc = rej.length ? <ul className="list-disc pl-4">{rej.slice(0, 5).map((x) => <li key={x.row}>Row {x.row} ({x.vendor}): {x.reason}</li>)}{rej.length > 5 && <li>… {rej.length - 5} more</li>}</ul> : undefined;
      (rej.length ? toast.warning : toast.success)(`${r.data.saved} mill countries saved${rej.length ? `, ${rej.length} rejected` : ""}`, { description: desc });
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" data-testid="mill-country-import-btn" disabled={busy} onClick={() => ref.current.click()}>{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-1.5 h-4 w-4" />}Import mill countries</Button>
      <input ref={ref} type="file" accept=".csv,.xlsx" className="hidden" data-testid="mill-country-import-input" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) run(f); }} />
    </>
  );
};

export const MillRefsPanel = ({ sid }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/suppliers/${sid}/mill-refs`).then((r) => setD(r.data)).catch(() => setD(false)); }, [sid]);
  if (!d) return null;
  const c = d.coverage;
  return (
    <Panel title="EUDR references on the mill's export customs declarations" testid="mill-refs-panel" className="mt-5">
      <p className="mb-3 text-sm text-slate-600" data-testid="mill-refs-coverage">
        {d.eu_dispatch ? `Dispatches from ${d.mill_country} (EU).` : `Dispatches from ${d.mill_country || "—"} (non-EU): export references aren't expected.`}{" "}
        Coverage: <b>{c.pct == null ? "—" : `${c.pct}%`}</b> of {fmtNum(c.po_lines)} in-scope PO lines ({fmtNum(c.covered)} matched by PO no. / material / date).
      </p>
      {d.refs.length ? (
        <table className="w-full text-sm" data-testid="mill-refs-table"><thead className="text-left text-[11px] uppercase tracking-wider text-slate-500"><tr>{["Reference", "Verification", "Delivery note", "Date", "Material", "Batch", "Qty", "PO", "Matched PO lines"].map((h) => <th key={h} className="py-1.5 pr-3">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">{d.refs.map((r) => <tr key={r.id}><td className="py-1.5 pr-3 font-mono text-xs">{r.reference}</td><td className="pr-3 font-mono text-xs">{r.verification || "—"}</td><td className="pr-3">{r.delivery_note || "—"}</td><td className="pr-3">{r.date || "—"}</td><td className="pr-3 font-mono text-xs">{r.material}</td><td className="pr-3">{r.batch || "—"}</td><td className="pr-3">{r.quantity ?? "—"}</td><td className="pr-3">{r.po_no || "—"}</td><td className={r.matched ? "text-emerald-700" : "text-amber-700"}>{r.matched ? r.po_lines : "not matched"}</td></tr>)}</tbody></table>
      ) : <p className="text-sm text-slate-500">No references received yet. The supplier adds them in the portal (step 5).</p>}
    </Panel>
  );
};

const TONE = { reimport_ref: "border-emerald-200 bg-emerald-50 text-emerald-900", reimport_missing: "border-amber-200 bg-amber-50 text-amber-900", mixed: "border-orange-200 bg-orange-50 text-orange-900", new_placing: "border-rose-200 bg-rose-50 text-rose-900" };

export const ReimportCard = ({ params, testid = "reimport-card" }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reimport/summary", { params }).then((r) => setD(r.data)).catch(() => setD(null)); }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  return (
    <Panel title="Re-import status — GB → NI / Ireland / other EU (material level, all in-scope lines; the snapshot counts in-stock items only)" testid={testid}>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Object.entries(d.sales).map(([k, v]) => (
          <div key={k} className={`rounded-xl border p-3 ${TONE[k]}`} data-testid={`${testid}-${k}`}>
            <p className="text-xs font-semibold">{v.label}</p>
            <p className="mt-1 font-display text-2xl font-semibold">{fmtNum(v.lines)} <span className="text-xs font-normal">lines</span></p>
            <p className="text-xs">{fmtNum(v.deliveries)} deliveries · NI {fmtNum(v.by_dest.NI || 0)} · IE {fmtNum(v.by_dest.IE || 0)} · EU {fmtNum(v.by_dest.EU || 0)}</p>
          </div>
        ))}
      </div>
      {Object.keys(d.po_9000).length > 0 && <p className="mt-3 text-xs text-slate-600" data-testid={`${testid}-po9000`}>France 9000 receipts from Euro Packaging UK (intercompany, looked through to the original mills): {Object.keys(d.labels).filter((k) => d.po_9000[k]).map((k) => `${k === "new_placing" ? d.fr_new_label : d.labels[k]} ${fmtNum(d.po_9000[k])}`).join(" · ")}</p>}
      <p className="mt-2 text-xs text-slate-500">{d.mixed_note} <Link to="/customs" className="text-[#D52B3B] hover:underline">Customs export</Link></p>
    </Panel>
  );
};
